/**
 * v3.15-1150 — Pending Call Documentation Watcher.
 * v3.18-1250 CALL-TRACK-002 — migrated from RequiredCallNotesModal
 * to the unified ActivityCompletionDialog so every inbound-call
 * wrap-up funnels through the Activity Completion Framework.
 *
 * Mounts once at the DashboardLayout level. Polls
 *   GET /api/inbound-calls/pending-dispositions/mine
 * every 30 seconds. If the rep has 1+ pending row, mount the
 * ActivityCompletionDialog for the OLDEST entry. The dialog's
 * onSubmit posts to the same disposition-complete endpoint the
 * previous button-grid modal used; on success we re-poll so
 * consecutive pending rows surface one at a time.
 *
 * Behaviour matches the user spec:
 *   - Real-time gate when the rep is actively in the Hub (polling
 *     cadence ≤30s after an inbound call ends).
 *   - The ClientDetailPage also drives this gate locally (see
 *     ClientDetailPage.js v3.18-1250 block) so the dispositions
 *     queued against a specific client are forced when the rep
 *     opens that file. Both mount sites use the SAME dialog.
 *   - Never silently skipped — the dialog is non-dismissible.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../lib/api';
import ActivityCompletionDialog from './ActivityCompletionDialog';

const POLL_INTERVAL_MS = 30000;

const PendingDispositionWatcher = ({ enabled = true }) => {
  const [active, setActive] = useState(null);
  const intervalRef = useRef(null);
  const inFlightRef = useRef(false);
  const location = useLocation();

  const poll = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const { data } = await api.get('/inbound-calls/pending-dispositions/mine');
      const items = data?.items || [];
      if (items.length > 0) {
        setActive((prev) => {
          // Only switch to a new entry if we don't already have one
          // open (the open dialog is exclusive).
          return prev || items[items.length - 1];   // oldest by sort
        });
      } else {
        setActive(null);
      }
    } catch (e) {
      // 401 or transport error — silent, retry next tick.
      console.debug('[v3.15-1150] pending-dispositions poll error', e?.response?.status || e?.message);
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    // Skip polling globally when the rep is already on the ClientDetailPage
    // because that page drives its OWN gate using the
    // /pending-dispositions/by-client/{id} endpoint (more specific +
    // works even with a stale parent polling cycle).
    const onClientDetail = /^\/clients\/[^/]+$/.test(location.pathname);
    if (onClientDetail) return undefined;

    poll();   // initial
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, location.pathname, poll]);

  const handleComplete = useCallback(() => {
    setActive(null);
    // Re-poll right away so consecutive pending rows surface immediately.
    setTimeout(poll, 200);
  }, [poll]);

  if (!enabled || !active) return null;
  return (
    <ActivityCompletionDialog
      open={true}
      onOpenChange={() => { /* non-dismissible gate — v3.15-1150 behaviour retained */ }}
      ruleEndpoint="/completion-rules/phone_call"
      itemTitle={`Incoming Call from ${active.from_number || 'unknown caller'}`}
      itemSubtitle={
        active.duration_sec
          ? `Duration ${Math.floor(active.duration_sec / 60)}m ${active.duration_sec % 60}s`
          : 'Document the outcome and notes to close this call.'
      }
      submitLabel="Complete Call"
      testIdPrefix="inbound-call-completion"
      onSubmit={async ({ outcome, notes }) => {
        await api.post(
          `/inbound-calls/pending-dispositions/${active.id}/complete`,
          { disposition: outcome, notes },
        );
        handleComplete();
      }}
    />
  );
};

export default PendingDispositionWatcher;
